import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { Editor, EditorState, RichUtils, CompositeDecorator, Modifier } from 'draft-js'

import { makeConverters } from './convert'

import FontColor, { styles as fontColors } from './controls/font_color'
import FontFamily, { styles as fontFamilies } from './controls/font_family'
import FontSize, { styles as fontSizes } from './controls/font_size'
import FontStyle, { styles as fontStyles } from './controls/font_style'
import DynamicField, { dynamicFieldDecorators } from './controls/dynamic_field'
import LinkField, { linkFieldDecorators } from './controls/link_field'

const styleMap = {
  ...fontColors,
  ...fontFamilies,
  ...fontSizes,
  ...fontStyles
}

const { toHTML, fromHTML } = makeConverters(styleMap)

export default class TextBlock extends Component {
  static propTypes = {
    dynamicFields: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
    singleline: PropTypes.bool
  }

  static defaultProps = {
    dynamicFields: [],
    onChange: x => x
  }

  compositeDecorator = new CompositeDecorator([
    ...dynamicFieldDecorators(this.props.dynamicFields),
    ...linkFieldDecorators
  ])

  constructor(props, ...args) {
    super(props, ...args)
    let editorState
    if (props.defaultValue) {
      editorState = EditorState.createWithContent(
        fromHTML(props.defaultValue),
        this.compositeDecorator
      )
    } else {
      editorState = EditorState.createEmpty(this.compositeDecorator)
    }
    this.state = { editorState }
  }

  handleChange = editorState => {
    this.props.singleline
      ? this.props.onChange(editorState.getCurrentContent().getPlainText())
      : this.props.onChange(toHTML(editorState.getCurrentContent()))

    this.setState({ editorState })
  }

  handleFocus = () => {
    this.editor.focus()
  }

  toggleStyleGroup = (styleGroup, toggling) => {
    const { editorState } = this.state
    const selection = editorState.getSelection()

    // Let's just allow one style at a time. Turn off all active styles form group.
    const nextContentState = Object.keys(styleGroup)
      .reduce((contentState, style) => {
        return Modifier.removeInlineStyle(contentState, selection, style)
      }, editorState.getCurrentContent())

    let nextEditorState = EditorState.push(
      editorState,
      nextContentState,
      'change-inline-style'
    )

    const currentStyle = editorState.getCurrentInlineStyle()

    // Unset style override for current style.
    if (selection.isCollapsed()) {
      nextEditorState = currentStyle.reduce((state, style) => {
        if (Object.keys(styleGroup).includes(style)) {
          return RichUtils.toggleInlineStyle(state, style)
        } else {
          return state
        }
      }, nextEditorState)
    }

    // If the style is being toggled on, apply it.
    if (toggling && !currentStyle.has(toggling)) {
      nextEditorState = RichUtils.toggleInlineStyle(
        nextEditorState,
        toggling
      )
    }

    this.handleChange(nextEditorState)
  }

  handlePaste = text => {
    if (this.props.singleline) {
      this.handleChange(EditorState.push(
        this.state.editorState,
        Modifier.replaceText(
          this.state.editorState.getCurrentContent(),
          this.state.editorState.getSelection(),
          text.replace(/\s*\n\s*/g, ' ')
        )
      ))
      return 'handled'
    } else {
      return 'not-handled'
    }
  }

  handleReturn = () => {
    if (this.props.singleline) {
      return 'handled'
    } else {
      return 'not-handled'
    }
  }

  render() {
    const className = classnames('text-block', {
      singleline: this.props.singleline
    })
    return (
      <div className={className}>
        <div className="controls">
          <FontStyle
            editorState={this.state.editorState}
            onChange={this.handleChange} />
          <FontColor
            editorState={this.state.editorState}
            toggleStyleGroup={this.toggleStyleGroup} />
          <FontSize
            editorState={this.state.editorState}
            toggleStyleGroup={this.toggleStyleGroup} />
          <FontFamily
            editorState={this.state.editorState}
            toggleStyleGroup={this.toggleStyleGroup} />
          <LinkField
            editorState={this.state.editorState}
            onChange={this.handleChange} />
          {!!this.props.dynamicFields.length && (
            <DynamicField
              dynamicFields={this.props.dynamicFields}
              editorState={this.state.editorState}
              onChange={this.handleChange} />
          )}
        </div>
        <div className="text" onFocus={this.handleFocus} ref={el => this.text = el} tabIndex="0">
          <Editor
            ref={el => this.editor = el}
            customStyleMap={styleMap}
            editorState={this.state.editorState}
            onChange={this.handleChange}
            handleReturn={this.handleReturn}
            handlePastedText={this.handlePaste} />
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// client/components/sections/website/settings/Form/RichText/TextBlock.js